import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Amount,
  IconErrorRoundedIndicator,
  IconInfoRoundedIndicator,
  IconTransferIncoming,
  IconTransferOutgoing,
  Stack,
  Text,
  unstable_Cell as Cell,
  unstable_CellList as CellList,
  useNotification,
} from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { generateInitials } from '@/shared/components/ProfileInitials'
import { formatCurrency, formatDate } from '@/shared/helpers'
import { type Transaction, getTransactions } from '@/features/transactions/api/transactions'
import { TransactionListSkeleton } from './TransactionListSkeleton'
import styles from './TransactionList.module.css'

const isCredit = (amount: number) => amount > 0

type TransactionCellProps = {
  transaction: Transaction
  locale: string | undefined
}

const TransactionCell = ({ transaction, locale }: TransactionCellProps) => {
  const { t } = useTranslation()
  const { amount, createdAt, counterpartyName, status } = transaction
  const credit = isCredit(amount.value)
  const isOnHold = status === 'ON_HOLD'
  const isRejected = status === 'REJECTED'

  const displayName = (() => {
    if (counterpartyName) {
      return credit
        ? t('transactions.fromCounterparty', { name: counterpartyName })
        : t('transactions.toCounterparty', { name: counterpartyName })
    }
    return credit ? t('transactions.incomingPayment') : t('transactions.outgoingPayment')
  })()

  const amountLabel = formatCurrency({ value: amount.value, currency: amount.currency, locale })
  const dateLabel = formatDate({ value: createdAt, locale })
  const spotColor = credit ? 'teal' : ('neutral' as const)

  const secondaryLabel = isRejected
    ? t('transactions.status.failed')
    : isOnHold
      ? t('transactions.status.processing')
      : dateLabel

  return (
    <Cell>
      <div className={styles.spotWrapper}>
        <Cell.Spot color={spotColor}>
          {counterpartyName ? (
            generateInitials(counterpartyName)
          ) : credit ? (
            <IconTransferIncoming />
          ) : (
            <IconTransferOutgoing />
          )}
        </Cell.Spot>
        {isRejected && (
          <div className={styles.statusBadge}>
            <IconErrorRoundedIndicator color='rhubarb' />
          </div>
        )}
        {isOnHold && (
          <div className={styles.statusBadge}>
            <IconInfoRoundedIndicator color='neutral' />
          </div>
        )}
      </div>
      <Cell.Main>
        <Stack direction='vertical' space='4'>
          <Text>{displayName}</Text>
          <Text variant='small' color={isRejected ? 'rhubarb' : 'neutral-alternate'}>
            {secondaryLabel}
          </Text>
        </Stack>
      </Cell.Main>
      <Amount color={credit ? 'teal' : 'neutral'} strikethrough={isRejected}>
        {amountLabel}
      </Amount>
    </Cell>
  )
}

export const TransactionList = () => {
  const { t, i18n } = useTranslation()
  const { notify } = useNotification()
  const locale = i18n.resolvedLanguage

  const { data, isPending, isError } = useQuery({
    queryKey: ['transactions'],
    queryFn: getTransactions,
  })

  useEffect(() => {
    if (!isError) return
    notify({
      id: 'transactions-error',
      title: t('transactions.errors.title'),
      description: t('transactions.errors.body'),
    })
  }, [isError, notify, t])

  const transactions = data?.transactions ?? []

  return (
    <Stack direction='vertical' space='8'>
      <Text variant='heading-x-small'>{t('transactions.title')}</Text>
      {isPending ? (
        <TransactionListSkeleton />
      ) : transactions.length === 0 ? (
        <Text color='neutral-alternate'>{t('transactions.empty')}</Text>
      ) : (
        <CellList>
          {transactions.map(tx => (
            <TransactionCell key={tx.id} transaction={tx} locale={locale} />
          ))}
        </CellList>
      )}
    </Stack>
  )
}
